/**
 * Voicemails view — list of voicemails captured for the tenant.
 *
 * Joel logs in here on day 1: a fresh voicemail from Tom is at the top of
 * the list, played via the standard <audio> element, with the transcription
 * text visible. Click → plays. (Authenticated proxy for Twilio media URLs
 * is a follow-up so the recording URL isn't a Twilio-credentials Basic-auth
 * link — for tonight we use the raw URL.)
 */

import { useCallback, useEffect, useState } from "react";
import { PhoneIncoming, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { formatE164ForDisplay } from "../lib/phone";

type Voicemail = {
  id: string;
  callSid: string;
  from: string;
  receivedAt: string;
  durationSec: number | null;
  recordingUrl: string | null;
  transcription: string | null;
};

export function VoicemailsView() {
  const [items, setItems] = useState<Voicemail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ items: Voicemail[] }>("/api/voicemails");
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15000); // poll every 15s for fresh voicemails
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="pad-fluid">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="text-3xl font-bold text-rogue-900">Voicemails</h1>
        <button
          onClick={() => void load()}
          className="text-sm text-rogue-600 hover:text-rogue-800 flex items-center gap-1"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg pad-fluid mb-4 text-sm">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-white rounded-xl shadow pad-fluid text-rogue-500 text-center">
          No voicemails yet. They appear here as soon as someone leaves one.
        </div>
      )}

      <div className="grid gap-fluid">
        {items.map((vm) => (
          <article key={vm.id} className="bg-white rounded-xl shadow pad-fluid">
            <header className="flex items-baseline justify-between gap-4 mb-2">
              <div className="flex items-center gap-2 text-rogue-900 font-medium">
                <PhoneIncoming className="w-4 h-4 text-emerald-700" />
                {formatE164ForDisplay(vm.from)}
              </div>
              <time className="text-xs text-rogue-500" dateTime={vm.receivedAt}>
                {new Date(vm.receivedAt).toLocaleString()}
              </time>
            </header>
            {vm.transcription ? (
              <p className="text-sm text-rogue-800 mb-3 italic">"{vm.transcription}"</p>
            ) : (
              <p className="text-xs text-rogue-500 mb-3">Transcription pending…</p>
            )}
            {vm.recordingUrl ? (
              <audio controls preload="metadata" className="w-full">
                {/* recordingUrl is `/api/recordings/<callSid>` — the authenticated
                    proxy. Append the format extension for Twilio. The Authorization
                    header is added by api.ts for fetch but <audio> uses the
                    native loader which does NOT pick that up — so the proxy
                    accepts cookie/session OR a one-time signed URL. MVP: proxy
                    requires Bearer, audio element will fail to load until we
                    add a signed-URL path. Tracked in SECURITY.md follow-ups. */}
                <source src={`${vm.recordingUrl}.mp3`} type="audio/mpeg" />
                <source src={`${vm.recordingUrl}.wav`} type="audio/wav" />
                Your browser does not support audio playback.
              </audio>
            ) : (
              <div className="text-xs text-rogue-500">Recording not yet available.</div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
